/* Coordinate theme navigation with Globo's own open/close actions.
 * Results, selected filters and menu subpanels remain owned by their components.
 */
(() => {
  'use strict';
  if (window.__collinoNavigationPanels) return;
  window.__collinoNavigationPanels = true;

  const root = document.documentElement;
  const sessions = new Map();
  let pending = null;
  let scheduled = false;
  let switching = false;
  let escapeHandled = false;
  const nodeFor = (kind) => document.getElementById({ menu: 'mobile-menu', search: 'glFilter-search-popup', filters: 'gf-tree' }[kind]);
  const isOpen = (kind) => {
    const node = nodeFor(kind);
    if (!node) return false;
    if (kind === 'menu') return node.classList.contains('active');
    if (kind === 'filters') return root.classList.contains('offcanvas-open');
    return node.getAttribute('aria-hidden') === 'false' && node.getClientRects().length > 0;
  };
  const visible = (node) => node?.isConnected && node.getClientRects().length > 0 &&
    !node.closest('[inert], [hidden], [aria-hidden="true"]') && getComputedStyle(node).visibility !== 'hidden';
  const returnTarget = (kind) => {
    if (kind === 'menu') return document.querySelector('summary.mobile-toggle');
    if (kind === 'filters') return document.querySelector('.gf-refine-toggle-mobile button, [data-gf-qf-drawer]');
    // Focusing the search input again reopens Globo. Return to its adjacent button.
    return document.querySelector('#header-search-input')?.form?.querySelector('button[type="submit"]');
  };
  const close = (kind) => {
    if (!isOpen(kind)) return;
    if (kind === 'menu') window.MobileMenuClose?.();
    else nodeFor(kind)?.querySelector(kind === 'search' ? '.gl-d-close-btn' : '.gf-close-canvas')?.click();
  };
  const prepare = (kind) => {
    // Globo refocuses its header input synchronously while removing the popup.
    // Keep the closing session until sync restores its original focus/scroll.
    if (isOpen(kind) || sessions.has(kind)) return;
    pending = { kind, y: window.scrollY, target: returnTarget(kind) };
    switching = true;
    for (const other of sessions.keys()) if (other !== kind) close(other);
    sync();
    switching = false;
  };
  const sync = () => {
    scheduled = false;
    const kinds = ['menu', 'search', 'filters'];
    const opened = kinds.filter(isOpen);
    const incoming = opened.find((kind) => !sessions.has(kind));
    if (incoming) {
      switching = true;
      for (const kind of sessions.keys()) if (kind !== incoming) close(kind);
      switching = false;
      sessions.set(incoming, pending?.kind === incoming ? pending : {
        kind: incoming, y: window.scrollY, target: returnTarget(incoming)
      });
      pending = null;
    }
    const anyOpen = kinds.some(isOpen);
    root.classList.toggle('collino-navigation-locked', anyOpen);
    for (const [kind, session] of sessions) {
      if (isOpen(kind)) continue;
      sessions.delete(kind);
      if (switching || anyOpen) continue;
      // Restore after the component has released its own lock. No smooth scroll.
      if (visible(session.target)) session.target.focus({ preventScroll: true });
      window.scrollTo({ top: session.y, behavior: 'instant' });
    }
    if (incoming === 'filters') {
      const panel = nodeFor('filters');
      panel?.querySelector('.gf-close-canvas')?.focus({ preventScroll: true });
    }
  };
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(sync);
  };
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (target.closest?.('summary.mobile-toggle')) prepare('menu');
    else if (target.closest?.('.gf-refine-toggle-mobile button, [data-gf-qf-drawer]')) prepare('filters');
    else if (target.closest?.('#cart-drawer-toggle')) {
      switching = true;
      for (const kind of sessions.keys()) close(kind);
      sync();
      switching = false;
    }
  }, true);
  document.addEventListener('focusin', (event) => {
    if (event.target.matches?.('#header-search-input')) prepare('search');
  }, true);
  document.addEventListener('keydown', (event) => {
    if (event.isComposing) return;
    const kind = ['search', 'filters'].find(isOpen);
    if (!kind) return; // The menu already implements its own keyboard trap.
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      escapeHandled = true;
      close(kind);
      sync();
      return;
    }
    if (event.key !== 'Tab') return;
    const panel = nodeFor(kind);
    const controls = [...panel.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')]
      .filter((node) => visible(node) && !node.disabled && node.tabIndex >= 0);
    if (!controls.length) return;
    const index = controls.indexOf(document.activeElement);
    if (index < 0 || (!event.shiftKey && index === controls.length - 1) || (event.shiftKey && index === 0)) {
      event.preventDefault();
      controls[event.shiftKey ? controls.length - 1 : 0].focus({ preventScroll: true });
    }
  }, true);
  document.addEventListener('keyup', (event) => {
    if (event.key !== 'Escape' || !escapeHandled) return;
    escapeHandled = false;
    event.stopImmediatePropagation();
  }, true);
  const observer = new MutationObserver((records) => {
    if (records.some((record) => record.type === 'childList' ||
      record.target === root || ['mobile-menu', 'glFilter-search-popup'].includes(record.target.id))) schedule();
  });
  observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'aria-hidden'] });
  window.addEventListener('resize', () => {
    if (window.matchMedia('(min-width: 1068px)').matches) close('menu');
    schedule();
  });
  document.addEventListener('shopify:section:unload', (event) => {
    for (const kind of sessions.keys()) if (event.target.contains(nodeFor(kind))) close(kind);
    schedule();
  });
  sync();
})();
